import React, { useMemo, useState, useEffect } from 'react';
import { Modal } from '../Common/Modal';
import Alert from '../Common/Alert';
import { VISIT_STATUS, VISIT_TYPES, formatVisitId } from '../../utils/constants';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import VisitModalHeader from './VisitModalHeader';
import './VisitActionsModal.css';

const VisitActionsModal = ({
  isOpen,
  onClose,
  visit,
  resolvedPiName,
  onRefresh, // reserved for future use
  onModify,
  onOpenConfirmPI,
  onOpenGenerateLetter,
  onOpenExecuteVisit,
  onStartReport,
  canSchedule = true,
  canVisitActivity = true,
  /** When true, user has ProfileActivityStageMapping for Completed (9999); enables View Report on that stage. */
  hasCompletedStageRights = false,
  canStartReport = false,
  isViewOnly = false,
  activeStage, // when 'schedule': show Modify; otherwise Schedule Visit modal is not available
}) => {
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!isOpen) setToast(null);
  }, [isOpen]);

  const visitDateLabel = useMemo(() => {
    if (!visit?.plannedStartDate) return '';
    try {
      return formatDateDDMMMYYYY(visit.plannedStartDate);
    } catch {
      return '';
    }
  }, [visit]);

  if (!visit) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Visit Actions">
        <p>No visit selected.</p>
      </Modal>
    );
  }

  const handleModifySchedule = () => {
    if (onModify) {
      onModify(visit);
    }
    onClose();
  };

  const handleConfirmPIAvailability = () => {
    if (!visit.scheduledVisitID) return;
    // CRA must generate the visit letter first before confirming letter action taken
    if (!visit.hasVisitLetter) {
      setToast({ type: 'info', message: 'Please generate the visit letter first before confirming letter action taken.' });
      return;
    }
    if (onOpenConfirmPI) {
      onOpenConfirmPI(visit);
    }
    onClose();
  };

  const handleGenerateVisitLetter = () => {
    if (!visit.scheduledVisitID) return;
    if (onOpenGenerateLetter) {
      onOpenGenerateLetter(visit);
    }
    onClose();
  };

  const handleExecuteVisit = () => {
    if (!visit?.scheduledVisitID) return;
    // Execute Visit: first check visit letter, then PI confirmation
    if (!visit.hasVisitLetter) {
      setToast({ type: 'info', message: 'Please generate the visit letter first before executing the visit.' });
      return;
    }
    if (visit.confirmationStatus !== 'Confirmed') {
      setToast({ type: 'info', message: 'Please confirm letter action taken first before executing the visit.' });
      return;
    }
    if (onOpenExecuteVisit) {
      onOpenExecuteVisit(visit);
    }
    onClose();
  };

  // Visibility: for a newly created visit, all 4 buttons appear in flow order.
  // For each specific visit, hide a button only after that operation has been performed for that visit.
  const status = visit.visitStatus;
  const alreadyExecuted = Boolean(visit.actualStartDate);
  const isScheduleStage =
    status === VISIT_STATUS.DRAFT ||
    status === VISIT_STATUS.PLANNED ||
    status === VISIT_STATUS.CONFIRMED;

  // Modify: show until visit is executed or PI is Approved (spec: If Approve then Modify Visit Schedule button will disappear)
  const isPiConfirmed = (visit.confirmationStatus ?? visit.ConfirmationStatus ?? '') === 'Confirmed';
  const canModify = isScheduleStage && !alreadyExecuted && !isPiConfirmed;

  // Generate Visit Letter: show until letter is generated and only before PI confirmation (hide after PI confirmed)
  const canGenerateLetter = isScheduleStage && !visit.hasVisitLetter && !isPiConfirmed;

  // Confirm PI: show until PI is confirmed for this visit
  const canConfirmPI = isScheduleStage && !isPiConfirmed;

  // Execute: show until visit is executed for this visit (show for Draft/Planned/Confirmed/Ongoing so new visit shows all 4)
  const canExecute =
    !alreadyExecuted &&
    (isScheduleStage || status === VISIT_STATUS.ONGOING);

  // In Progress: visit executed (Ongoing) and in report stage — show Continue to open CRF
  const isInProgressStage = status === VISIT_STATUS.ONGOING && alreadyExecuted;
  const canShowContinue = isInProgressStage && canStartReport && onStartReport;

  const titleVisitType =
    VISIT_TYPES[visit.visitType] || visit.visitType || '';

  // Site display: use siteCode (API sends vSiteCode for SIV/SMV/SCV; for SSV sends vSSVSiteCode after onboarding, vSiteCode before)
  const siteDisplay = visit.siteCode || (visit.siteName || '').replace(/^Site\s+/i, '') || '';
  const titleLine = `${formatVisitId(visit)} (${titleVisitType}) `;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      title={`Visit Actions - ${visitDateLabel}`}
      className="visit-actions-modal"
    >
      <VisitModalHeader
        projectCode={visit.projectCode}
        siteName={visit.siteName}
        siteCode={visit.siteCode ?? visit.SiteCode}
        piName={resolvedPiName ?? visit.piName ?? visit.PIName ?? visit.pIName}
      />
      <div className="visit-actions-header">
        <div className="visit-actions-subtitle">
          {titleLine}
        </div>
       
      </div>

      <div className="visit-actions-buttons">
        {canShowContinue && (
          <button
            className="btn btn-primary btn-block"
            onClick={() => {
              if (onStartReport) onStartReport(visit);
              onClose();
            }}
          >
            {isViewOnly ? 'View' : 'Continue'}
          </button>
        )}

        {activeStage === 'closed' && hasCompletedStageRights && onStartReport && (
          <button
            type="button"
            className="btn btn-secondary btn-block"
            onClick={() => {
              onStartReport(visit);
              onClose();
            }}
          >
            View Report
          </button>
        )}

        {canModify && canSchedule && activeStage === 'schedule' && (
          <button
            className="btn btn-primary btn-block"
            onClick={handleModifySchedule}
          >
            Modify Schedule Visit
          </button>
        )}

        {canGenerateLetter && canVisitActivity && (
          <button
            className="btn btn-primary btn-block"
            onClick={handleGenerateVisitLetter}
          >
            Generate Visit Letter
          </button>
        )}

        {canConfirmPI && canVisitActivity && (
          <button
            className="btn btn-primary btn-block"
            onClick={handleConfirmPIAvailability}
          >
            Confirm Letter Action Taken
          </button>
        )}

        {canExecute && canVisitActivity && (
          <button
            className="btn btn-primary btn-block"
            onClick={handleExecuteVisit}
          >
            Execute Visit
          </button>
        )}
      </div>
    </Modal>
    {toast && (
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
        <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      </div>
    )}
  </>
  );
};

export default VisitActionsModal;

